import React, { Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stage } from '@react-three/drei';
import * as THREE from 'three';
import { useDesignStore } from '../../store/designStore';
import { ModelErrorBoundary, TexturedModel, useDesignOverlayTexture } from './Model3DScene';
import { VariantSelector } from './VariantSelector';
import { ComingSoonOverlay } from './ComingSoonOverlay';

const ROTATE_STEP = Math.PI / 12; // 15° per click

// `showVariantSelector` defaults to true (the designer's 3D tab and the PDP 3D
// widget both let the shopper switch colors/materials). The customer approval
// page passes false: there the customer is signing off on one specific design,
// not reconfiguring it, so no variant picker.
//
// `defaultAutoRotate` defaults to false — the designer's own 3D tab (and the
// PDP widget) is a working view where a spinning model fights the user's own
// drag/rotate-pad input while they're actively looking/reconfiguring. The
// read-only preview contexts (?mode=widget, the CustomEdge admin's widget
// modal, the approval page — all via DesignPreviewCanvas.tsx) pass true: no
// one is dragging anything there, so a slow spin is a better default glance.
// The toggle button always lets the viewer stop/start it either way.
export function Model3DPreview({ showVariantSelector = true, defaultAutoRotate = false }: { showVariantSelector?: boolean; defaultAutoRotate?: boolean } = {}) {
  const { t } = useTranslation();
  const { productConfig, variants, selectedVariantOptions } = useDesignStore();
  const textureUrl = useDesignOverlayTexture();
  const [failed, setFailed] = useState(false);
  const controlsRef = useRef<any>(null);
  const [autoRotate, setAutoRotate] = useState(defaultAutoRotate);
  // Bumping this remounts <Canvas> (via its `key`), which re-runs Stage's
  // `adjustCamera` framing — the simplest reliable way to get back to a known-good
  // camera position/zoom without hand-tracking OrbitControls' internal state.
  const [resetTick, setResetTick] = useState(0);

  // Scales the camera's offset from the orbit target by `factor` (<1 zooms in,
  // >1 zooms out) and re-applies it — works regardless of which three.js/drei
  // version's OrbitControls is installed, unlike calling its internal
  // `dollyIn`/`dollyOut` (not part of the stable public API across versions).
  const zoomBy = (factor: number) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const camera = controls.object;
    camera.position.sub(controls.target).multiplyScalar(factor).add(controls.target);
    controls.update();
  };

  // Orbits the camera around the target by `azimuthDelta` (horizontal, "<"/">")
  // and `polarDelta` (vertical, "^"/"v") radians — converts the camera's current
  // offset from the target into spherical coordinates, nudges the angles, then
  // converts back, the same manual-offset approach `zoomBy` above uses (rather
  // than OrbitControls' internal rotate methods, not part of its stable public
  // API across versions). `phi` (polar angle) is clamped just short of the poles
  // so repeated up/down clicks can't flip the camera upside down.
  const rotateBy = (azimuthDelta: number, polarDelta: number) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const camera = controls.object;
    const offset = camera.position.clone().sub(controls.target);
    const spherical = new THREE.Spherical().setFromVector3(offset);
    spherical.theta += azimuthDelta;
    spherical.phi = THREE.MathUtils.clamp(spherical.phi + polarDelta, 0.01, Math.PI - 0.01);
    offset.setFromSpherical(spherical);
    camera.position.copy(controls.target).add(offset);
    camera.lookAt(controls.target);
    controls.update();
  };

  if (!productConfig?.glbModelUrl) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
        {t('designer.preview3d.unavailable')}
      </div>
    );
  }

  if (failed) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
        {t('designer.preview3d.loadFailed')}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, background: '#f0f0f0', position: 'relative' }}>
      <Canvas key={`${productConfig.glbModelUrl}-${resetTick}`} camera={{ position: [0, 0, 3], fov: 45 }}>
        <Suspense fallback={null}>
          <ModelErrorBoundary onError={() => setFailed(true)}>
            <Stage environment="city" intensity={0.5} adjustCamera>
              <TexturedModel url={productConfig.glbModelUrl} designOverlayUrl={textureUrl} variants={variants} selectedVariantOptions={selectedVariantOptions} />
            </Stage>
          </ModelErrorBoundary>
        </Suspense>
        <OrbitControls ref={controlsRef} autoRotate={autoRotate} enableZoom enablePan makeDefault />
      </Canvas>

      <ComingSoonOverlay />

      {showVariantSelector && (
        <div style={{ position: 'absolute', top: 16, right: 16 }}>
          <VariantSelector layout="inline" inlineMode="all" />
        </div>
      )}

      {/* Both control clusters grouped bottom-right: the rotate pad sits to
          the left of the zoom/auto-rotate/reset column rather than all the
          way across the screen on its own. */}
      <div style={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        {/* Manual rotate pad — full 3×3 grid, 8 directions (4 cardinal +
            4 diagonal) orbiting the camera around the model; only the
            center cell is empty. Diagonals nudge both axes at once. */}
        <div style={{
          display: 'grid', gridTemplateColumns: '32px 32px 32px', gridTemplateRows: '32px 32px 32px', gap: 4,
        }}>
          <button onClick={() => rotateBy(-ROTATE_STEP, -ROTATE_STEP)} title={t('designer.preview3d.rotateUpLeft')} style={controlBtn}>↖</button>
          <button onClick={() => rotateBy(0, -ROTATE_STEP)} title={t('designer.preview3d.rotateUp')} style={controlBtn}>↑</button>
          <button onClick={() => rotateBy(ROTATE_STEP, -ROTATE_STEP)} title={t('designer.preview3d.rotateUpRight')} style={controlBtn}>↗</button>
          <button onClick={() => rotateBy(-ROTATE_STEP, 0)} title={t('designer.preview3d.rotateLeft')} style={controlBtn}>←</button>
          <div />
          <button onClick={() => rotateBy(ROTATE_STEP, 0)} title={t('designer.preview3d.rotateRight')} style={controlBtn}>→</button>
          <button onClick={() => rotateBy(-ROTATE_STEP, ROTATE_STEP)} title={t('designer.preview3d.rotateDownLeft')} style={controlBtn}>↙</button>
          <button onClick={() => rotateBy(0, ROTATE_STEP)} title={t('designer.preview3d.rotateDown')} style={controlBtn}>↓</button>
          <button onClick={() => rotateBy(ROTATE_STEP, ROTATE_STEP)} title={t('designer.preview3d.rotateDownRight')} style={controlBtn}>↘</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button onClick={() => zoomBy(0.85)} title={t('designer.preview3d.zoomIn')} style={controlBtn}>+</button>
          <button onClick={() => zoomBy(1.15)} title={t('designer.preview3d.zoomOut')} style={controlBtn}>−</button>
          <button
            onClick={() => setAutoRotate(v => !v)}
            title={t('designer.preview3d.toggleRotate')}
            style={{ ...controlBtn, color: autoRotate ? 'var(--primary, #222222)' : '#374151' }}
          >
            <OrbitIcon />
          </button>
          <button onClick={() => setResetTick(n => n + 1)} title={t('designer.preview3d.resetView')} style={controlBtn}>
            <ResetViewIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

const controlBtn: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 6, border: '1px solid #e2e8f0', background: '#ffffff',
  color: '#374151', fontSize: 16, fontWeight: 600, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
};

// A dashed ellipse (the "orbit path") with a small arrowhead — reads as
// "spin the object", unlike the generic ↻ reload glyph it replaces.
function OrbitIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <ellipse cx="8" cy="8" rx="6.5" ry="3.2" strokeDasharray="2.2 1.8" />
      <circle cx="8" cy="8" r="1.3" fill="currentColor" stroke="none" />
      <path d="M13 6.2 L14.3 5.1 L14.5 6.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// A target/crosshair-in-viewfinder mark — reads as "recenter the camera",
// unlike the ⤢ diagonal-resize glyph it replaces (which looks like "expand").
function ResetViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M1.5 5V2.5a1 1 0 0 1 1-1H5" strokeLinecap="round" />
      <path d="M11 1.5h2.5a1 1 0 0 1 1 1V5" strokeLinecap="round" />
      <path d="M14.5 11v2.5a1 1 0 0 1-1 1H11" strokeLinecap="round" />
      <path d="M5 14.5H2.5a1 1 0 0 1-1-1V11" strokeLinecap="round" />
      <circle cx="8" cy="8" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}
